import { LogicalNode, QueryNode } from "modules/record/record-producer/types/query.types";
import { parseRule, RuleNode, RuleOperator } from "modules/gate/form-runtime/engine/ruleParser";

export const FILTER_TO_RULE_OPERATOR: Record<string, RuleOperator> = {
    eq: "==", neq: "!=", gt: ">", gte: ">=", lt: "<", lte: "<=",
    contains: "contains", empty: "empty", not_empty: "not_empty",
};
const RULE_TO_FILTER_OPERATOR = Object.fromEntries(Object.entries(FILTER_TO_RULE_OPERATOR).map(([filter, rule]) => [rule, filter]));
export const SUPPORTED_FILTER_OPERATORS = Object.keys(FILTER_TO_RULE_OPERATOR);
const fieldNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const compileNode = (node: QueryNode): string => {
    if (node.type === "exists") throw new Error("EXISTS is not supported by form rules");
    if (node.type === "logical") {
        const parts = node.conditions.map(compileNode).filter(Boolean);
        if (!parts.length) return "";
        if (parts.length === 1) return parts[0];
        return `(${parts.join(node.operator === "and" ? " && " : " || ")})`;
    }
    if (!node.field || !node.operator) return "";
    if (!fieldNamePattern.test(node.field)) throw new Error(`Invalid rule field: ${node.field}`);
    const operator = FILTER_TO_RULE_OPERATOR[node.operator];
    if (!operator) throw new Error(`Unsupported rule operator: ${node.operator}`);
    if (operator === "empty" || operator === "not_empty") return `${node.field} ${operator}`;
    if (node.value === undefined || node.value === null) return "";
    const value = typeof node.value === "number" || typeof node.value === "boolean"
        ? String(node.value)
        : `'${String(node.value).replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
    return `${node.field} ${operator} ${value}`;
};

export const compileRuleCondition = (node: LogicalNode): string => compileNode(node);

let nextId = 0;
const convertRuleNode = (node: RuleNode): QueryNode => {
    if (node.type === "condition") return {
        id: `rule-${++nextId}`, type: "condition", field: node.field,
        operator: RULE_TO_FILTER_OPERATOR[node.operator], value: node.value,
    };
    if (node.type === "logical") return {
        id: `rule-${++nextId}`, type: "logical", operator: node.operator === "&&" ? "and" : "or",
        conditions: [convertRuleNode(node.left), convertRuleNode(node.right)],
    };
    throw new Error("Literal or invalid rule cannot be edited as conditions");
};

export const parseRuleCondition = (expression: string): LogicalNode => {
    if (!expression.trim()) return { id: "rule-root", type: "logical", operator: "and", conditions: [] };
    const converted = convertRuleNode(parseRule(expression));
    return converted.type === "logical" ? converted : { id: "rule-root", type: "logical", operator: "and", conditions: [converted] };
};
